<script lang="ts">
	import type { Waypoint } from '$lib/components/map/types';
	import type { Currency } from '$lib/components/types';
	import { beforeNavigate, invalidate } from '$app/navigation';
	import { page } from '$app/state';
	import {
		ItineraryConstraint,
		ItineraryTravelMode,
		type UpdateItinerariesFavoriteInput,
		UpdateItinerariesFavoriteStore,
	} from '$houdini';
	import Button from '$lib/components/atoms/button/Button.svelte';
	import Icon from '$lib/components/icons/Icon.svelte';
	import { getMapLayoutContext } from '$lib/components/map/map-layout-context';
	import { addNotification } from '$lib/components/notifications/notifications';
	import { defaultNotificationCloseButtonProps, FAVORITES_MAX_NB } from '$lib/components/profile/favorite/constant';
	import Card from '$lib/components/route/routeSelector/components/Card.svelte';
	import { PROFILE_FAVORITES } from '$lib/constants/route';
	import { trackAddToFavorites, trackShowFavorites } from '$lib/services/tracking/googleAnalytics/favorites';
	import { isProfilePage, isRouteWithoutPOIs } from '$lib/services/urls/routeIds';
	import { getSearchParamsStore, gotoRelativeUrl } from '$lib/services/urls/searchParams';
	import { useCarStorage } from '$lib/utils/carStorage.svelte';
	import { usePersistentState } from '$lib/utils/persistentState.svelte';
	import { useQueuedCallback } from '$lib/utils/queuedCallbacks.svelte';
	import * as m from '$paraglide/messages';

	interface Props {
		disabled?: boolean;
	}

	const { disabled }: Props = $props();

	const searchParams = getSearchParamsStore();
	const { waypointsInfo, itineraryResultsStore, userProfileStore } = page.data;
	const isUserLoggedIn = $derived(!!page.data.userSession);
	const { tripOptionsStore } = getMapLayoutContext();
	// We don't have a specific request to get (one) car info based on ID, so this is a small step towards local first app here, we store the info in local storage
	const { saveCarInfo } = useCarStorage();
	// Only used as a way to snapshot the saved routes when navigating to the route results pages (cost, roadsheet etc).
	const persistedSavedRoutes = usePersistentState<string[]>('savedRoutes', [], { storageType: 'session' });
	const queuedCallback = useQueuedCallback();

	beforeNavigate((navigation) => {
		const shouldResetSavedRoutes = navigation.to?.url && (!isRouteWithoutPOIs(navigation.to.route) || isProfilePage(navigation.to.route));
		if (shouldResetSavedRoutes) {
			persistedSavedRoutes.setCurrent([]);
		}
	});

	let updateItinerariesFavoriteStore: UpdateItinerariesFavoriteStore;
	let savedRoutes = $state(persistedSavedRoutes.getCurrent());
	let requestState = $state<'idle' | 'loading' | 'error'>('idle');
	const isSaved = $derived(savedRoutes.includes($itineraryResultsStore?.routes[$searchParams?.selectedRoute ?? 0]?.identifier ?? ''));
	const userProfile = $userProfileStore;

	function onMutationError() {
		requestState = 'error';
		addNotification({
			message: m.bookmark__iti_save_error(),
			closeButton: {
				...defaultNotificationCloseButtonProps,
				text: m.bookmark__iti_save_error__cta(),
			},
			onCloseCallback: () => {
				createFavorite();
			},
		});
	}

	function onMutationSuccess(updatedSavedRoutes: string[]) {
		addNotification({
			message: m.bookmark__itinerary_saved(),
			closeButton: {
				...defaultNotificationCloseButtonProps,
				text: m.modal__close(),
			},
		});
		requestState = 'idle';
		persistedSavedRoutes.setCurrent(updatedSavedRoutes);
		savedRoutes = updatedSavedRoutes;
		invalidate('loadUser:init');
	}

	function onFavoriteLimitReached() {
		trackAddToFavorites(userProfile?.data?.me.itinerariesFavorites?.length ?? 0);
		addNotification({
			message: m.bookmark__favorite_limit_reached(),
			closeButton: {
				...defaultNotificationCloseButtonProps,
				text: m.bookmark__favorite_limit_reached__cta(),
			},
			onCloseCallback: () => {
				trackShowFavorites('limit_reached', userProfile?.data?.me.itinerariesFavorites?.length ?? 0);
				redirectToUserFavorites();
			},
		});
	}

	function setAddressForMyLocation(waypoint: Waypoint) {
		return `${waypoint?.name ? `${waypoint.name}, ` : ''}${waypoint?.city}`;
	}

	function computeDataForFavorite() {
		if (!$itineraryResultsStore?.routes || !waypointsInfo) {
			return;
		}

		const { departure, arrival, waypoints } = waypointsInfo;

		const isDepartureMyLocation = departure.name === m.itinerary__current_location();
		const isArrivalMyLocation = arrival.name === m.itinerary__current_location();

		const departureName = isDepartureMyLocation ? (setAddressForMyLocation($itineraryResultsStore.waypoints[0]) ?? departure.name) : departure.name;
		const arrivalName = isArrivalMyLocation ? (setAddressForMyLocation($itineraryResultsStore.waypoints[$itineraryResultsStore?.waypoints.length - 1]) ?? arrival.name) : arrival.name;

		const selectedRoute = $itineraryResultsStore?.routes[$searchParams?.selectedRoute ?? 0];
		const coordinates = $itineraryResultsStore?.waypoints.map(wp => wp.location).filter(Boolean);

		const steps = waypoints.length > 2
			? waypoints
				.filter((wp, index) => wp?.label && index !== 0 && index !== waypoints.length - 1)
				.map((wp, index) => {
					if (wp.label === m.itinerary__current_location()) {
						return setAddressForMyLocation($itineraryResultsStore.waypoints[index + 1]);
					}
					return wp.label;
				}) as string[]
			: [];

		const car = $searchParams.car ?? $tripOptionsStore.car ?? null;

		if (car) {
			saveCarInfo(car.id, {
				model: car.model,
				maker: car.maker,
				isEv: car.isEv,
				energy: car.energy,
			});
		}

		return {
			input: {
				departureName,
				arrivalName,
				steps,
				coordinates,
				mode: $searchParams.travelMode ?? $tripOptionsStore.travelMode,
				distance: selectedRoute.routeDistance,
				duration: selectedRoute.duration,
				totalCost: {
					amount: selectedRoute.totalCost.amount,
					currency: selectedRoute.totalCost.currency.toLowerCase() as Currency,
				},
				carId: car?.id ?? '',
				constraints: $searchParams.tripConstraint || $tripOptionsStore.tripConstraint || ItineraryConstraint.NONE,
				isEv: selectedRoute?.isEv ?? false,
				withCaravan: $searchParams?.withCaravan ?? false,
			} satisfies UpdateItinerariesFavoriteInput,
			selectedRoute,
		};
	}

	async function saveFavorite(computedData: ReturnType<typeof computeDataForFavorite>) {
		const { input, selectedRoute } = computedData ?? {};

		if (!input) {
			requestState = 'idle';
			return;
		}

		updateItinerariesFavoriteStore ??= new UpdateItinerariesFavoriteStore();

		try {
			const { data } = await updateItinerariesFavoriteStore.mutate({ input });

			if (selectedRoute?.identifier && data) {
				onMutationSuccess([...savedRoutes, selectedRoute.identifier]);
			}
		} catch (error) {
			console.error(`An error occurred while saving favorite: ${error}`);
			onMutationError();
		}
	}

	function createFavorite() {
		const usersFavoritesLength = $userProfileStore?.data?.me.itinerariesFavorites?.length;

		if (usersFavoritesLength >= FAVORITES_MAX_NB) {
			onFavoriteLimitReached();
			return;
		}

		const computedFavorite = computeDataForFavorite();

		if (!computedFavorite) {
			return;
		}

		trackAddToFavorites(usersFavoritesLength ? usersFavoritesLength + 1 : 0);
		requestState = 'loading';
		saveFavorite(computedFavorite);
	}

	function redirectUserToLoginModal() {
		const data = computeDataForFavorite();
		queuedCallback.enqueue(() => saveFavorite(data));
		trackAddToFavorites(0);
		$searchParams.showModal = 'login';
		$searchParams.redirectUserTo = `${page.url.pathname}?${page.url.search}`;
	}

	function redirectToUserFavorites() {
		gotoRelativeUrl(PROFILE_FAVORITES);
	}

	function handleClick() {
		if (isSaved) {
			trackShowFavorites('favorite_added', $userProfileStore?.data?.me.itinerariesFavorites?.length ?? 0);
			redirectToUserFavorites();
			return;
		}

		if (isUserLoggedIn) {
			createFavorite();
			return;
		}

		redirectUserToLoginModal();
	}
</script>

{#if ([ItineraryTravelMode.CAR, ItineraryTravelMode.MOTORCYCLE] as string[]).includes($searchParams.travelMode || $tripOptionsStore.travelMode)}
	<Card
		dataTestId="favorites-card"
		class={['grid-col-[1/-1] pt-1! px-3! h-full']}
	>
		<Button
			class={['p-x-0 p-y-2.5 border-primary-weak rounded-sm text-primary justify-start', 'w-full', isSaved && 'flex items-center flex-row-reverse justify-between']}
			labelClasses="p-0!"
			iconClasses="size-4! text-4!"
			variant="ghost"
			size="sm"
			icon={isSaved ? undefined : 'bookmark-plus'}
			text={isSaved ? m.bookmark__see() : m.bookmark__add__itinerary()}
			iconPosition="left"
			onclick={handleClick}
			dataTestId="favorites-cta"
			disabled={disabled || requestState === 'loading'}
		>
			{#snippet badge()}
				{#if isSaved}
					<span class="flex justify-start items-center gap-1 text-3.5 line-height-4 text-coloredneutral">
						<Icon
							icon="bookmark-check"
							class="size-4! text-4"
						/>
						{m.bookmark__itinerary__saved()}
					</span>
				{/if}
			{/snippet}
		</Button>
	</Card>
{/if}
